import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

// Schema for validating the token input
const verifyTokenSchema = z.object({
  token: z.string().min(6, "Must be at least 6 digits").max(8, "Must be at most 8 digits"),
});

type VerifyTokenFormValues = z.infer<typeof verifyTokenSchema>;

export type MFASetupProps = {
  onComplete?: () => void;
  onCancel?: () => void;
};

export function MFASetup({ onComplete, onCancel }: MFASetupProps) {
  const [step, setStep] = useState<"initial" | "setup" | "verify" | "backupCodes" | "complete">("initial");
  const [secret, setSecret] = useState<string>("");
  const [qrCode, setQrCode] = useState<string>("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form for token verification
  const form = useForm<VerifyTokenFormValues>({
    resolver: zodResolver(verifyTokenSchema),
    defaultValues: {
      token: "",
    },
    mode: "onSubmit",
  });

  // Query to check MFA status
  const { data: mfaStatus, isLoading: statusLoading } = useQuery({
    queryKey: ["/api/mfa/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/mfa/status");
      if (!res.ok) throw new Error("Failed to get MFA status");
      return res.json();
    },
  });

  // Mutation to set up MFA
  const setupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/mfa/setup", {});
      if (!res.ok) throw new Error("Failed to set up MFA");
      return res.json();
    },
    onSuccess: (data) => {
      setSecret(data.secret);
      setQrCode(data.qrCode);
      setStep("setup");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mutation to verify and enable MFA
  const verifyMutation = useMutation({
    mutationFn: async (token: string) => {
      const res = await apiRequest("POST", "/api/mfa/enable", {
        token,
        secret,
      });
      if (!res.ok) throw new Error("Failed to verify token");
      return res.json();
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setStep("backupCodes");
      // Invalidate MFA status query
      queryClient.invalidateQueries({ queryKey: ["/api/mfa/status"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle token submission
  const onSubmit = (values: VerifyTokenFormValues) => {
    verifyMutation.mutate(values.token);
  };

  // Handle final completion
  const handleComplete = () => {
    setStep("complete");
    onComplete?.();
  };

  // If already enabled, show message
  if (statusLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading MFA status...</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center p-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (mfaStatus?.enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>Your account is already protected with two-factor authentication.</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="border-green-500">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertTitle>MFA is enabled</AlertTitle>
            <AlertDescription>
              Your account is protected with two-factor authentication. You'll need to enter a verification code when you sign in.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      {step === "initial" && (
        <>
          <CardHeader>
            <CardTitle>Set Up Two-Factor Authentication</CardTitle>
            <CardDescription>
              Add an extra layer of security to your account by enabling two-factor authentication.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  You'll need an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator to complete this setup.
                </AlertDescription>
              </Alert>
              <div className="text-sm">
                <p className="mb-2">Two-factor authentication adds an extra layer of security to your account by requiring:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Your password</li>
                  <li>A verification code from your authenticator app</li>
                </ol>
                <p className="mt-2">This helps protect your account even if your password is compromised.</p>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button
              onClick={() => setupMutation.mutate()}
              disabled={setupMutation.isPending}
              className="w-full"
            >
              {setupMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                "Begin Setup"
              )}
            </Button>
          </CardFooter>
        </>
      )}

      {step === "setup" && (
        <>
          <CardHeader>
            <CardTitle>Scan QR Code</CardTitle>
            <CardDescription>
              Scan this QR code with your authenticator app
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={qrCode} alt="QR Code" className="max-w-full h-48 border rounded" />
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
                <p className="text-xs text-gray-700 dark:text-gray-300 font-mono break-all">
                  If you can't scan the QR code, enter this code manually: {secret}
                </p>
              </div>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="token"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Verification Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter the 6-digit code"
                            {...field}
                            autoComplete="off"
                          />
                        </FormControl>
                        <FormDescription>
                          Enter the 6-digit code from your authenticator app
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    disabled={verifyMutation.isPending}
                    className="w-full"
                  >
                    {verifyMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify & Enable"
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </CardContent>
        </>
      )}

      {step === "backupCodes" && (
        <>
          <CardHeader>
            <CardTitle>Save Your Backup Codes</CardTitle>
            <CardDescription>
              Keep these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  Each backup code can only be used once. Keep them safe and secure!
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <code key={index} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-md text-center font-mono">
                    {code}
                  </code>
                ))}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We recommend saving these codes in a secure password manager or printing them and storing them in a safe place.
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={handleComplete} className="w-full">
              I've Saved My Backup Codes
            </Button>
          </CardFooter>
        </>
      )}

      {step === "complete" && (
        <>
          <CardHeader>
            <CardTitle>Setup Complete</CardTitle>
            <CardDescription>
              Two-factor authentication is now enabled for your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="border-green-500">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>
                Your account is now protected with two-factor authentication. You'll need to enter a verification code when you sign in.
              </AlertDescription>
            </Alert>
          </CardContent>
        </>
      )}
    </Card>
  );
}